import type { ICharacter, ICharacterPower, ICharacterPowerComponent, IResource, IPowerUsage } from '../../entities/types';
import { MODIFIER_DEFS, POWER_DEFS } from '../../entities/gameDataLoaders';
import { calculateComponentPricing, calculatePowerPricing } from './mathEngine';
import { getResourcePowers } from './resourcePowers';
import { isDeviceResource } from './resourceCalculations';
import { resolveEffectiveDuration } from './effectParameters';
import { getCharacterStrength } from './componentRanks';
import { pricingStrengthForSources, type PricingStrength } from './strengthContributions';

export type UsageCharacter = Pick<ICharacter, 'powers' | 'equipment' | 'resourceLinks' | 'powerUsage' | 'abilities' | 'absentAbilities'>;
export interface PowerSource {
  key: string; power: ICharacterPower; name: string; equipment: boolean;
  personal: boolean; resource?: IResource;
  pricingStrength?: PricingStrength;
}
export function getPowerSources(character: UsageCharacter, resources: readonly IResource[] = []): PowerSource[] {
  const linkedIds = new Set(character.resourceLinks?.map(link => link.resourceId));
  const sources: PowerSource[] = [
    ...character.powers.map(power => ({ key: `power:${power.id}`, power, name: power.name, equipment: false, personal: true })),
    ...(character.equipment ?? []).map(power => ({ key: `equipment:${power.id}`, power, name: power.name, equipment: true, personal: true })),
    ...resources.filter(resource => linkedIds.has(resource.id)).flatMap(resource => getResourcePowers(resource).map(({ power }) => ({
      key: `resource:${resource.id}:${power.id}`, power, resource, name: `${resource.name} · ${power.name}`,
      equipment: !isDeviceResource(resource), personal: resource.type !== 'vehicle' && resource.type !== 'headquarters',
    }))),
  ];
  return sources.map(source => {
    const resource = source.resource;
    const context = resource?.type === 'vehicle' || resource?.type === 'headquarters'
      ? sources.filter(item => item.resource?.id === resource.id).map(item => ({ ...item, personal: true, equipment: false }))
      : sources;
    const natural = resource?.type === 'vehicle' ? resource.strength : resource?.type === 'headquarters' ? 0 : getCharacterStrength(character);
    return { ...source, pricingStrength: (power, components) => character.absentAbilities.includes('str') && source.personal ? 0
      : pricingStrengthForSources(natural, context, power, components) };
  });
}
export function isPermanentComponent(component: ICharacterPowerComponent): boolean {
  const effect = POWER_DEFS.find(def => def.id === component.effectId);
  return !!effect && resolveEffectiveDuration(effect.duration, component, { effect, modifierDefs: MODIFIER_DEFS }).value === 'permanent';
}
export function powerBranches(power: ICharacterPower) {
  return [{ id: 'base', name: power.name, dynamic: !!power.baseDynamic, components: power.components },
    ...power.alternateEffects.map(alternate => ({ ...alternate }))];
}
export function resolvePowerUsage(source: PowerSource, usage: IPowerUsage = {}) {
  const branches = powerBranches(source.power);
  const branch = branches.find(item => item.id === (usage.branchId ?? 'base'));
  const warnings: { key: string; params?: Record<string, string | number> }[] = [];
  if (!branch) warnings.push({ key: 'traits.invalidBranch', params: { name: source.name } });
  const personal = usage.recipient ? usage.recipient === 'character' : source.personal;
  let components: ICharacterPowerComponent[] = [];
  if (branch) {
    if (branch.dynamic && usage.allocations) {
      components = branches.filter(item => item.dynamic).flatMap(item => item.components.flatMap(component => {
        const requested = usage.allocations?.[component.id] ?? 0;
        if (requested > component.ranks) warnings.push({ key: 'traits.rankAllocation', params: { name: source.name } });
        const ranks = Math.min(component.ranks, Math.max(0, requested));
        return ranks > 0 ? [{ ...component, ranks }] : [];
      }));
      const strength = allocationStrength(source, components);
      const cost = components.reduce((sum, component) => {
        const effect = POWER_DEFS.find(def => def.id === component.effectId);
        return sum + (effect ? calculateComponentPricing(component, effect, MODIFIER_DEFS, strength).total : 0);
      }, 0);
      const budget = calculatePowerPricing(source.power, POWER_DEFS, MODIFIER_DEFS, source.pricingStrength).mainCost;
      if (cost > budget) warnings.push({ key: 'traits.arrayBudget', params: { name: source.name, cost, budget } });
    } else components = branch.components;
  }
  if (usage.enabled === false) components = components.filter(isPermanentComponent);
  return { components, personal, warnings, branchId: branch?.id ?? 'base' };
}

/** Allocation preview uses the same component prices and base pool as the rules resolver. */
export function powerAllocationSummary(source: PowerSource, usage: IPowerUsage = {}) {
  const allocation = resolvePowerUsage(source, { ...usage, enabled: true });
  const strength = allocationStrength(source, allocation.components);
  return {
    cost: allocation.components.reduce((sum, component) => {
      const effect = POWER_DEFS.find(def => def.id === component.effectId);
      return sum + (effect ? calculateComponentPricing(component, effect, MODIFIER_DEFS, strength).total : 0);
    }, 0),
    budget: calculatePowerPricing(source.power, POWER_DEFS, MODIFIER_DEFS, source.pricingStrength).mainCost,
  };
}

function allocationStrength(source: PowerSource, components: ICharacterPowerComponent[]): number {
  return typeof source.pricingStrength === 'function' ? source.pricingStrength(source.power, components) : source.pricingStrength ?? 0;
}
